import { z } from "zod";
import { actorSchema, idSchema, instantSchema, moneySchema, sha256Schema } from "@amber/contracts";

const textSchema = z.string().min(1);
const scoreSchema = z.number().min(0).max(1);
const languageCodeSchema = z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/, "expected a language code");
export const routeRefSchema = z.string().regex(/^[a-z0-9][a-z0-9._-]*@\d+$/, "expected a versioned route reference");

export const msTupleSchema = z
  .tuple([z.number().int().nonnegative(), z.number().int().positive()])
  .refine(([start, end]) => start < end, "range start must be before its end");
export type MsTuple = z.infer<typeof msTupleSchema>;

export const TRANSCRIPTION_JOB_STATES = ["queued", "running", "waiting", "completed", "failed", "cancelled"] as const;
export const transcriptionJobStateSchema = z.enum(TRANSCRIPTION_JOB_STATES);
export const TRANSCRIPTION_WAITING_REASONS = ["budget", "quota", "pause"] as const;

export const transcriptionJobRecordSchema = z
  .strictObject({
    id: idSchema("trj"),
    source_version_id: idSchema("ver"),
    item_id: idSchema("itm"),
    route_ref: routeRefSchema,
    state: transcriptionJobStateSchema,
    waiting: z
      .strictObject({
        reason: z.enum(TRANSCRIPTION_WAITING_REASONS),
        resume_condition: textSchema,
      })
      .nullable(),
    provider_job_ids: z.array(textSchema),
    result_id: idSchema("res").nullable(),
    attempt_ids: z.array(idSchema("att")),
    queued_at: instantSchema,
    finished_at: instantSchema.nullable(),
  })
  .refine((j) => (j.state === "waiting") === (j.waiting !== null), "waiting is set exactly when the job is waiting")
  .refine((j) => j.state !== "completed" || j.result_id !== null, "a completed job names its result")
  .refine(
    (j) => (j.state === "completed" || j.state === "failed" || j.state === "cancelled") === (j.finished_at !== null),
    "finished_at is set exactly when the job is finished",
  );
export type TranscriptionJobRecord = z.infer<typeof transcriptionJobRecordSchema>;

export const NAMING_DECISIONS = ["assigned", "ambiguous", "unresolved"] as const;
export const PROFILE_DECISIONS = ["matched", "ambiguous", "no_match", "not_applicable"] as const;

const evidenceRefsSchema = z.array(textSchema);

export const namingDecisionSchema = z.discriminatedUnion("decision", [
  z.strictObject({
    decision: z.literal("assigned"),
    name: textSchema,
    confidence: scoreSchema,
    evidence: evidenceRefsSchema.min(1),
  }),
  z.strictObject({
    decision: z.literal("ambiguous"),
    name: z.null(),
    confidence: z.null(),
    alternatives: z
      .array(z.strictObject({ name: textSchema, confidence: scoreSchema, evidence: evidenceRefsSchema }))
      .min(2),
    evidence: evidenceRefsSchema,
    reason: textSchema,
  }),
  z.strictObject({
    decision: z.literal("unresolved"),
    name: z.null(),
    confidence: z.null(),
    evidence: evidenceRefsSchema,
    reason: textSchema,
  }),
]);
export type NamingDecision = z.infer<typeof namingDecisionSchema>;

export const profileDecisionSchema = z.discriminatedUnion("decision", [
  z.strictObject({
    decision: z.literal("matched"),
    profile_id: idSchema("prf"),
    confidence: scoreSchema,
  }),
  z.strictObject({
    decision: z.literal("ambiguous"),
    profile_id: z.null(),
    candidates: z.array(idSchema("prf")).min(2),
    confidence: z.null(),
    reason: textSchema,
  }),
  z.strictObject({
    decision: z.literal("no_match"),
    profile_id: z.null(),
    confidence: z.null(),
    reason: textSchema,
  }),
  z.strictObject({
    decision: z.literal("not_applicable"),
    profile_id: z.null(),
    confidence: z.null(),
  }),
]);
export type ProfileDecision = z.infer<typeof profileDecisionSchema>;

export const resultSpeakerSchema = z.strictObject({
  id: idSchema("spk"),
  label: textSchema,
  naming: namingDecisionSchema,
  profile: profileDecisionSchema,
});
export type ResultSpeaker = z.infer<typeof resultSpeakerSchema>;

export const resultEvidenceSchema = z.discriminatedUnion("kind", [
  z.strictObject({ id: textSchema, kind: z.literal("metadata"), locator: textSchema }),
  z.strictObject({ id: textSchema, kind: z.literal("audio"), range_ms: msTupleSchema }),
]);

export const languageRangeSchema = z.union([
  z.strictObject({ range_ms: msTupleSchema, language: languageCodeSchema, confidence: scoreSchema.nullable() }),
  z.strictObject({
    range_ms: msTupleSchema,
    language: z.strictObject({ state: z.literal("unknown"), reason: textSchema }),
    confidence: z.null(),
  }),
]);

export const resultRunSchema = z.strictObject({
  provider: textSchema,
  model: textSchema,
  adapter_version: textSchema,
  provider_job_id: textSchema.nullable(),
  status: z.enum(["completed", "failed", "cancelled"]),
  cost: moneySchema.nullable(),
  started_at: instantSchema,
  finished_at: instantSchema.nullable(),
});

export const resultWordSchema = z
  .strictObject({
    id: textSchema,
    text: textSchema,
    start_ms: z.number().int().nonnegative(),
    end_ms: z.number().int().positive(),
    confidence: scoreSchema.nullable(),
    speaker: idSchema("spk").nullable(),
    language: languageCodeSchema.nullable(),
  })
  .refine((w) => w.start_ms < w.end_ms, "start_ms must be before end_ms");

export const candidateResultRecordSchema = z
  .strictObject({
    id: idSchema("res"),
    schema_version: z.string().regex(/^0\.\d+$/, "unsupported schema version"),
    source_version_id: idSchema("ver"),
    audio_ref: z.strictObject({
      artifact_id: idSchema("art"),
      sha256: sha256Schema,
    }),
    created_at: instantSchema,
    base_result_id: idSchema("res").nullable(),
    correction_revision: textSchema.nullable(),
    route_ref: routeRefSchema,
    languages: z.array(languageRangeSchema),
    coverage: z.strictObject({
      processed_ranges_ms: z.array(msTupleSchema),
      missing_ranges_ms: z.array(
        z.strictObject({
          range_ms: msTupleSchema,
          reason: textSchema,
          hallucination_flagged: z.boolean(),
        }),
      ),
      outcome: z.enum(["full", "partial"]),
    }),
    speakers: z.array(resultSpeakerSchema),
    evidence: z.array(resultEvidenceSchema),
    runs: z.array(resultRunSchema).min(1),
    word_count: z.number().int().nonnegative(),
    words_sample: z.array(resultWordSchema),
  })
  .superRefine((r, ctx) => {
    const evidenceIds = new Set(r.evidence.map((e) => e.id));
    const speakerIds = new Set<string>(r.speakers.map((s) => s.id));
    if (evidenceIds.size !== r.evidence.length)
      ctx.addIssue({ code: "custom", path: ["evidence"], message: "evidence ids must be unique" });
    if (speakerIds.size !== r.speakers.length)
      ctx.addIssue({ code: "custom", path: ["speakers"], message: "speaker ids must be unique" });
    r.speakers.forEach((s, i) => {
      const refs =
        s.naming.decision === "ambiguous"
          ? [...s.naming.evidence, ...s.naming.alternatives.flatMap((a) => a.evidence)]
          : s.naming.evidence;
      for (const ref of refs) {
        if (!evidenceIds.has(ref))
          ctx.addIssue({
            code: "custom",
            path: ["speakers", i, "naming", "evidence"],
            message: `unknown evidence ${ref}`,
          });
      }
    });
    r.words_sample.forEach((w, i) => {
      if (w.speaker !== null && !speakerIds.has(w.speaker))
        ctx.addIssue({ code: "custom", path: ["words_sample", i, "speaker"], message: "unknown speaker" });
    });
  });
export type CandidateResultRecord = z.infer<typeof candidateResultRecordSchema>;

export const PASSAGE_CHECK_STATES = ["requested", "running", "completed", "failed_to_run"] as const;
export const PASSAGE_CHECK_OUTCOMES = ["passed", "failed", "insufficient", "not_run"] as const;
const passageCheckOutcomeSchema = z.enum(PASSAGE_CHECK_OUTCOMES);

export const passageCheckRecordSchema = z
  .strictObject({
    id: idSchema("chk"),
    scope: z.strictObject({
      source_version_id: idSchema("ver"),
      range_ms: msTupleSchema,
      communication_id: idSchema("com"),
      evidence_revision_id: idSchema("rev"),
      context: textSchema,
    }),
    policy: textSchema,
    method: textSchema,
    state: z.enum(PASSAGE_CHECK_STATES),
    outcomes: z.strictObject({
      wording: passageCheckOutcomeSchema,
      locator: passageCheckOutcomeSchema,
      context: passageCheckOutcomeSchema,
    }),
    failure_detail: textSchema.optional(),
    verified: z.boolean(),
    invalidated: z
      .strictObject({
        at: instantSchema,
        cause: z.discriminatedUnion("kind", [
          z.strictObject({ kind: z.literal("correction"), correction_id: idSchema("cor") }),
          z.strictObject({ kind: z.literal("source_version"), source_version_id: idSchema("ver") }),
          z.strictObject({ kind: z.literal("revision"), revision_id: idSchema("rev") }),
        ]),
      })
      .nullable(),
    requested_by: actorSchema,
    idempotency_key: textSchema,
    requested_at: instantSchema,
    completed_at: instantSchema.nullable(),
  })
  .refine(
    (c) => c.verified === (c.state === "completed" && Object.values(c.outcomes).every((o) => o === "passed")),
    "verified exactly when the check completed and every dimension passed",
  )
  .refine(
    (c) => c.state === "completed" || Object.values(c.outcomes).every((o) => o === "not_run"),
    "only a completed check has outcomes",
  )
  .refine(
    (c) => (c.state === "completed" || c.state === "failed_to_run") === (c.completed_at !== null),
    "completed_at is set exactly when the check ended",
  );
export type PassageCheckRecord = z.infer<typeof passageCheckRecordSchema>;

export const exampleRecordSchemas: Record<string, z.ZodType> = {
  "transcription-jobs.json": transcriptionJobRecordSchema,
  "candidate-results.json": candidateResultRecordSchema,
  "passage-checks.json": passageCheckRecordSchema,
};
